// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — CopilotOverlay
// Wraps document viewer content and shows CopilotButton on
// hover over h1/h2 headings. Requires M9+M10 active.
// ═══════════════════════════════════════════════════════════════

import { useState, useRef, useCallback, useEffect, type ReactNode } from 'react';
import { CopilotButton } from './CopilotButton';

interface CopilotOverlayProps {
  codigoDocumento: string;
  children: ReactNode;
  enabled: boolean;
}

interface HoveredHeading {
  top: number;
  text: string;
  id: string;
}

export function CopilotOverlay({ codigoDocumento, children, enabled }: CopilotOverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<HoveredHeading | null>(null);
  const [showButton, setShowButton] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimer();
    hideTimerRef.current = setTimeout(() => {
      setShowButton(false);
      // Delay clearing hovered to allow popover to stay open
      setTimeout(() => setHovered(null), 200);
    }, 300);
  }, [clearHideTimer]);

  // Mouse delegation on container
  useEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container) return;

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const heading = target.closest('h1, h2') as HTMLElement | null;

      if (heading && container.contains(heading)) {
        clearHideTimer();
        const containerRect = container.getBoundingClientRect();
        const headingRect = heading.getBoundingClientRect();
        const top = headingRect.top - containerRect.top;
        const text = heading.textContent?.trim() || '';
        const id = heading.id || `heading-${top}`;

        setHovered({ top, text, id });
        setShowButton(true);
      }
    };

    const handleMouseOut = (e: MouseEvent) => {
      const related = e.relatedTarget as HTMLElement | null;
      if (related && container.contains(related)) {
        const heading = related.closest('h1, h2');
        if (!heading) {
          scheduleHide();
        }
      } else {
        scheduleHide();
      }
    };

    container.addEventListener('mouseover', handleMouseOver);
    container.addEventListener('mouseout', handleMouseOut);

    return () => {
      container.removeEventListener('mouseover', handleMouseOver);
      container.removeEventListener('mouseout', handleMouseOut);
      clearHideTimer();
    };
  }, [enabled, clearHideTimer, scheduleHide]);

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative' }}
      onMouseEnter={clearHideTimer}
      onMouseLeave={scheduleHide}
    >
      {children}
      {showButton && hovered && (
        <CopilotButton
          codigoDocumento={codigoDocumento}
          seccionId={hovered.id}
          seccionTexto={hovered.text}
          position={{ top: hovered.top, right: -4 }}
        />
      )}
    </div>
  );
}
